import { clampRange, maxWindowSize, rangeLength, type RowRange } from './rowRange'
import type { RowData } from '@/api/types'

/**
 * The rows the client is holding.
 *
 * A dense slice of the view -- `rows[i]` is the row at absolute index
 * `range.start + i` -- plus the total the backend last reported. Every function
 * here takes a window and returns a new one; nothing mutates, nothing fetches,
 * and nothing knows what a component is. That is what makes the hard part of
 * this rewrite testable against plain arrays.
 *
 * Dense rather than sparse on purpose. Upstream had two engines: a sparse array
 * the length of the whole table, which allocated a million slots for a million
 * rows, and this one. This is the one that scaled, so it is the only one here.
 */
export interface RowWindow {
  /** The rows held, in view order, with no gaps. */
  rows: RowData[]
  /** Which absolute indexes those rows occupy. */
  range: RowRange
  /** Total rows in the view, as the backend last reported it. */
  count: number
  /**
   * Identifies the query these rows answer: the view, its filters, its sorts,
   * its group-bys and the search term.
   *
   * Carried on the window so a response that arrives after any of them changed
   * can be recognised as stale and dropped. Upstream compared the view id alone,
   * which catches switching views but not a filter changing while a scroll fetch
   * is in flight -- the fetch then lands rows that the new filter excludes.
   */
  signature: string
}

export function emptyWindow(signature = ''): RowWindow {
  return { rows: [], range: { start: 0, end: 0 }, count: 0, signature }
}

/** Whether an absolute row index is one this window holds. */
export function holdsIndex(window: RowWindow, index: number): boolean {
  return index >= window.range.start && index < window.range.end
}

/** The row at an absolute index, or undefined when it is outside the window. */
export function rowAt(window: RowWindow, index: number): RowData | undefined {
  if (!holdsIndex(window, index)) return undefined
  return window.rows[index - window.range.start]
}

/** Where a row sits in the whole view, or -1 when it is not held. */
export function indexOfRow(window: RowWindow, rowId: number): number {
  const offset = window.rows.findIndex((row) => row.id === rowId)
  return offset === -1 ? -1 : window.range.start + offset
}

/**
 * Replaces the window wholesale, for a first load or a query that changed.
 */
export function resetWindow(
  rows: RowData[],
  start: number,
  count: number,
  signature: string,
): RowWindow {
  return {
    rows: [...rows],
    range: { start, end: start + rows.length },
    count,
    signature,
  }
}

/**
 * Folds a fetched page into the window.
 *
 * The page either extends the window on one side or replaces it. A page that
 * neither touches nor overlaps the held range would leave a hole, which a dense
 * buffer cannot represent -- so it replaces instead, which is the correct
 * outcome for the jump that produced it.
 */
export function applyFetchedRows(
  window: RowWindow,
  rows: RowData[],
  start: number,
  count: number,
  options: { requestSize?: number } = {},
): RowWindow {
  const incoming: RowRange = { start, end: start + rows.length }
  const held = window.range

  if (rows.length === 0) {
    return { ...window, count }
  }

  const touchesBefore = incoming.end >= held.start && incoming.start <= held.start
  const touchesAfter = incoming.start <= held.end && incoming.end >= held.end

  if (window.rows.length === 0 || (!touchesBefore && !touchesAfter)) {
    return resetWindow(rows, start, count, window.signature)
  }

  let merged: RowData[]
  let range: RowRange

  if (touchesBefore) {
    // The overlap is re-fetched data for rows already held; the incoming copy
    // wins, because it is newer.
    const keepFrom = incoming.end - held.start
    merged = [...rows, ...window.rows.slice(Math.max(0, keepFrom))]
    range = { start: incoming.start, end: Math.max(held.end, incoming.end) }
  } else {
    const keepUntil = incoming.start - held.start
    merged = [...window.rows.slice(0, keepUntil), ...rows]
    range = { start: held.start, end: incoming.end }
  }

  return trimToLimit(
    { rows: merged, range, count, signature: window.signature },
    incoming.start >= held.start ? 'front' : 'back',
    options.requestSize,
  )
}

/**
 * Drops rows from one end when the window has grown past its limit.
 *
 * Which end matters: rows are dropped away from where the reader is, so the
 * direction they were just scrolling stays covered.
 */
export function trimToLimit(
  window: RowWindow,
  dropFrom: 'front' | 'back',
  requestSize?: number,
): RowWindow {
  const limit = maxWindowSize(requestSize)
  const excess = window.rows.length - limit
  if (excess <= 0) return window

  if (dropFrom === 'front') {
    return {
      ...window,
      rows: window.rows.slice(excess),
      range: { start: window.range.start + excess, end: window.range.end },
    }
  }

  return {
    ...window,
    rows: window.rows.slice(0, limit),
    range: { start: window.range.start, end: window.range.end - excess },
  }
}

/**
 * A row was created somewhere in the view.
 *
 * Where it lands decides what happens. Before the window, only the indexes
 * shift -- the row itself is not held and must not be, or the buffer stops being
 * a contiguous slice. Inside, it is inserted and the far end gives way. After,
 * nothing changes but the count.
 *
 * The count moves in all three cases, which is the part that is easy to miss:
 * the scrollbar is sized from it, so a row inserted a thousand rows above must
 * still lengthen the view.
 */
export function insertRow(
  window: RowWindow,
  row: RowData,
  index: number,
  options: { requestSize?: number } = {},
): RowWindow {
  const count = window.count + 1

  if (index < window.range.start) {
    return {
      ...window,
      count,
      range: { start: window.range.start + 1, end: window.range.end + 1 },
    }
  }

  if (index >= window.range.end) {
    // One past the end is still an append while the window reaches the bottom of
    // the view, because the row becomes the new last row of what is held.
    if (index === window.range.end && window.range.end === window.count) {
      const rows = [...window.rows, row]
      return trimToLimit(
        { ...window, rows, count, range: { ...window.range, end: window.range.end + 1 } },
        'front',
        options.requestSize,
      )
    }
    return { ...window, count }
  }

  const offset = index - window.range.start
  const rows = [...window.rows.slice(0, offset), row, ...window.rows.slice(offset)]

  return trimToLimit(
    { ...window, rows, count, range: { ...window.range, end: window.range.end + 1 } },
    'back',
    options.requestSize,
  )
}

/**
 * A row was deleted somewhere in the view.
 *
 * A row outside the window still changes the count and, when it was above,
 * shifts every held index down by one -- which is the case a naive
 * "find it and splice it" misses entirely, leaving the window pointing one row
 * further down the table than it really is.
 */
export function deleteRow(window: RowWindow, rowId: number, index?: number): RowWindow {
  const held = indexOfRow(window, rowId)

  if (held !== -1) {
    const offset = held - window.range.start
    return {
      ...window,
      rows: [...window.rows.slice(0, offset), ...window.rows.slice(offset + 1)],
      count: Math.max(0, window.count - 1),
      range: { start: window.range.start, end: window.range.end - 1 },
    }
  }

  const count = Math.max(0, window.count - 1)

  if (index !== undefined && index < window.range.start) {
    return {
      ...window,
      count,
      range: { start: window.range.start - 1, end: window.range.end - 1 },
    }
  }

  return { ...window, count }
}

/** Replaces a held row in place, leaving the window's shape alone. */
export function updateRow(window: RowWindow, row: RowData): RowWindow {
  const offset = window.rows.findIndex((held) => held.id === row.id)
  if (offset === -1) return window

  const rows = [...window.rows]
  rows[offset] = row
  return { ...window, rows }
}

/**
 * Moves a held row to a new absolute index.
 *
 * A row that moves out of the window is dropped rather than carried along: the
 * window is a contiguous slice, and keeping a row that now sorts a thousand rows
 * away would make it a lie.
 */
export function moveRow(window: RowWindow, rowId: number, toIndex: number): RowWindow {
  const from = indexOfRow(window, rowId)
  if (from === -1) return window

  const offset = from - window.range.start
  const row = window.rows[offset]
  if (row === undefined) return window

  const without = [...window.rows.slice(0, offset), ...window.rows.slice(offset + 1)]

  if (toIndex < window.range.start || toIndex >= window.range.end) {
    return {
      ...window,
      rows: without,
      range: { start: window.range.start, end: window.range.end - 1 },
    }
  }

  const target = Math.max(0, Math.min(toIndex - window.range.start, without.length))
  return {
    ...window,
    rows: [...without.slice(0, target), row, ...without.slice(target)],
  }
}

/**
 * The count changed without any row event to explain it.
 *
 * A refetch reports how many rows the view now has, and it can be fewer than the
 * window is holding -- a filter was tightened, or rows were deleted by somebody
 * else while this tab was not listening. The window is then clamped to what
 * still exists, which is what stops a virtualizer from rendering rows past the
 * end of a table that has shrunk.
 */
export function applyCount(window: RowWindow, count: number): RowWindow {
  if (count === window.count) return window

  const range = clampRange(window.range, count)
  const rows = window.rows.slice(0, rangeLength(range))

  return {
    ...window,
    count,
    range: { start: range.start, end: range.start + rows.length },
    rows,
  }
}

/** Whether a response still answers the question the window is asking. */
export function isStale(window: RowWindow, signature: string): boolean {
  return window.signature !== signature
}
